/**
 * Pipeline-Flow — deklarative State-Machine mit STEP-LEVEL Branching.
 *
 * Phasen enthalten Steps, Steps haben ihre eigenen Transitions. Default: jeder
 * Step geht zum nächsten Step in derselben Phase. Spezielle Steps (Phase 2
 * Step 7 = Conflict-Pre-Check, Phase 7 Step 3 = Resolve) verzweigen explizit
 * mit branches[].
 *
 * Single-Source-of-Truth für:
 *  - WerkstattPage Footer-Button (nextPhaseFor)
 *  - Tab-Bar Done/Reachable (phaseDone, phaseReachable)
 *  - Pipeline-Diagram (lib/pipelineMermaid → Subgraphs mit Steps)
 *  - BPMN-Export (geplant)
 */
import type { Card, Timesheet } from "./api";
import { roomStatus, type CardLike as RoomCardLike, type CardKind as RoomCardKind, type RoomStatus } from "./rooms";

export type PhaseSlug = "upload" | "ocr" | "validierung" | "berechnung" | "review" | "export" | "konflikt";
export type FlowTarget =
  | { kind: "phase"; phase: PhaseSlug }
  | { kind: "step"; phase: PhaseSlug; step: number }
  | { kind: "end" }
  | { kind: "wait" };

export interface StepTransition {
  when: (card: Card, timesheet: Timesheet | null) => boolean;
  to: FlowTarget;
  label: string;
}

export interface PhaseStep {
  num: number;
  label: string;
  /** Optional — wenn der Step branched. Sonst: default → next-step-in-phase. */
  branches?: StepTransition[];
  /** Markiert User-Gate-Step (Owner-Klick nötig, kein Auto-Transition). */
  userGate?: boolean;
}

export interface PhaseFlow {
  slug: PhaseSlug;
  num: number;
  title: string;
  steps: PhaseStep[];
  done: (card: Card, timesheet: Timesheet | null) => boolean;
  /** Phase-Level Fallback-Transitions (greift wenn letzter Step kein eigenes branches[] hat). */
  endTransitions: StepTransition[];
  parallel?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function hasOcrOutput(t: Timesheet | null): boolean {
  const days = (t?.data as any)?.days;
  return !!days && Object.keys(days).some(k => /^\d+$/.test(k));
}
function hasCalcOutput(t: Timesheet | null): boolean {
  return !!(t?.data as any)?.calculation;
}
const TERMINAL = new Set(["archived", "superseded"]);
const POST_OCR = new Set(["ocr_complete", "validated", "calculated", "exported"]);
const POST_VALIDATION = new Set(["validated", "calculated", "exported"]);
const POST_CALC = new Set(["calculated", "exported"]);

// ─── The flow itself ──────────────────────────────────────────────────────

export const PIPELINE_FLOW: PhaseFlow[] = [
  {
    slug: "upload",
    num: 1,
    title: "Upload + Triage",
    steps: [
      { num: 1, label: "PDF-Upload" },
      { num: 2, label: "Page-Triage (Kind-Klassifikation)" },
      { num: 3, label: "Bereit für Phase 2", userGate: true },
    ],
    done: (c) =>
      c.state !== "uploaded" &&
      c.state !== "pending_review" &&
      c.state !== "pending",
    endTransitions: [
      { when: (c) => c.state === "pending_conflict", to: { kind: "step", phase: "konflikt", step: 1 }, label: "Konflikt erkannt" },
      { when: (c) => TERMINAL.has(c.state),         to: { kind: "end" },                              label: "Karte beendet" },
      { when: (c) => POST_OCR.has(c.state) || c.state === "ocr_pending" || c.state === "ocr_running",
                                                     to: { kind: "step", phase: "ocr", step: 1 },     label: "OCR starten" },
      { when: () => true,                            to: { kind: "wait" },                            label: "Warte auf Triage / OCR-Start" },
    ],
  },
  {
    slug: "ocr",
    num: 2,
    title: "OCR",
    steps: [
      { num: 1, label: "PDF rendern (220 DPI)" },
      { num: 2, label: "Form-Typ detektieren" },
      { num: 3, label: "Grid-Detection (4-Pass)" },
      { num: 4, label: "Per-Cell OCR (K3)" },
      { num: 5, label: "Header-OCR + Auto-Match" },
      { num: 6, label: "Symbol-OCR (K/U/F/ZA/…)" },
      // Step 7: BRANCHING — entweder OK→Validierung oder Konflikt→Phase 7 Step 3
      {
        num: 7,
        label: "Conflict-Pre-Check + State-Transition",
        branches: [
          { when: (c) => c.state === "pending_conflict", to: { kind: "step", phase: "konflikt", step: 1 }, label: "Konflikt → Resolver" },
          { when: (c) => TERMINAL.has(c.state),         to: { kind: "end" },                                label: "Karte beendet" },
          { when: (c) => POST_VALIDATION.has(c.state) || c.state === "ocr_complete",
                                                         to: { kind: "step", phase: "validierung", step: 1 }, label: "kein Konflikt → Validierung" },
          { when: () => true,                            to: { kind: "wait" },                              label: "OCR läuft noch" },
        ],
      },
    ],
    done: (c, t) => hasOcrOutput(t) || POST_OCR.has(c.state) || c.state === "pending_conflict" || TERMINAL.has(c.state),
    endTransitions: [],  // Step 7 hat seine eigenen branches
  },
  {
    slug: "validierung",
    num: 3,
    title: "Validierung",
    steps: [
      { num: 1, label: "Confidence-Marker" },
      { num: 2, label: "Plausibilität (Rule A/B/C)" },
      { num: 3, label: "Header-OCR + Auto-Match (Re-View)" },
      { num: 4, label: "User-Validierung", userGate: true },
    ],
    // Phase 3 done = User hat validiert (state moved to validated). archived/superseded
    // bedeuten NICHT, dass diese Phase durchlaufen wurde — Discard passiert VOR Validierung.
    done: (c) => POST_VALIDATION.has(c.state),
    endTransitions: [
      { when: (c) => TERMINAL.has(c.state),     to: { kind: "end" },                                label: "Karte beendet" },
      { when: (c) => POST_CALC.has(c.state) || c.state === "validated",
                                                 to: { kind: "step", phase: "berechnung", step: 1 }, label: "Berechnung" },
      { when: () => true,                        to: { kind: "wait" },                              label: "Warte auf User-Validate-Klick" },
    ],
  },
  {
    slug: "berechnung",
    num: 4,
    title: "Berechnung",
    steps: [
      { num: 1, label: "Karte + Periode auswählen" },
      { num: 2, label: "Soll-Schedule laden" },
      { num: 3, label: "KV-Snapshot zum Stichtag" },
      { num: 4, label: "Pro-Tag-Berechnung" },
      { num: 5, label: "Biweekly-Mapping" },
      { num: 6, label: "Wochen-Aggregation" },
      { num: 7, label: "Status-Aggregat" },
      { num: 8, label: "Monats-Total + Hinweise" },
      { num: 9, label: "RZL-Export-Preview" },
    ],
    // Phase 4 done = Worker hat calculation produziert. archived/superseded bedeuten
    // nicht dass die Phase durchlief.
    done: (c, t) => hasCalcOutput(t) || POST_CALC.has(c.state),
    endTransitions: [
      { when: (c) => TERMINAL.has(c.state),     to: { kind: "end" },                            label: "Karte beendet" },
      { when: (c) => POST_CALC.has(c.state),    to: { kind: "step", phase: "review", step: 1 }, label: "Review" },
      { when: () => true,                        to: { kind: "wait" },                          label: "Warte auf Worker-Calc" },
    ],
  },
  {
    slug: "review",
    num: 5,
    title: "Review",
    steps: [
      { num: 1, label: "Karten-Status für Monat" },
      { num: 2, label: "Monats-Total der Karte" },
      { num: 3, label: "Hinweise sichtbar" },
      { num: 4, label: "Bereit für Export", userGate: true },
    ],
    // Phase 5 (Review) done = Karte ist exportiert.
    // Vorher: "Review = passiver Indikator, ab calculated grün". Das brach
    // Glass-Box-Konsistenz (User-Feedback 2026-05-18): Phase-Tab ✓ obwohl
    // Step 4 noch pending war. Korrigiert: tab ✓ erst wenn der Review-Gate
    // implizit durchlaufen wurde (= export ist passiert). Terminal-States
    // (archived/superseded) heißen NICHT dass Review stattfand.
    done: (c) => c.state === "exported",
    endTransitions: [
      { when: (c) => TERMINAL.has(c.state),         to: { kind: "end" },                            label: "Karte beendet" },
      { when: (c) => c.state === "exported",       to: { kind: "end" },                            label: "Bereits exportiert" },
      { when: (c) => c.state === "calculated",     to: { kind: "step", phase: "export", step: 1 }, label: "Export starten" },
      { when: () => true,                            to: { kind: "wait" },                          label: "Warte auf Owner-Klick" },
    ],
  },
  {
    slug: "export",
    num: 6,
    title: "Export RZL",
    steps: [
      { num: 1, label: "Export-Readiness prüfen" },
      { num: 2, label: "RZL-Workbook bauen" },
      { num: 3, label: "State → exported" },
      { num: 4, label: "Download zum User", userGate: true },
    ],
    // Phase 6 done = Export wurde durchgeführt (state=exported). archived bedeutet
    // explizit NICHT, dass exportiert wurde.
    done: (c) => c.state === "exported",
    endTransitions: [
      { when: (c) => c.state === "exported",     to: { kind: "end" }, label: "Pipeline abgeschlossen" },
      { when: (c) => TERMINAL.has(c.state),     to: { kind: "end" }, label: "Karte beendet" },
      { when: () => true,                        to: { kind: "wait" }, label: "Warte auf Export-Klick" },
    ],
  },
  {
    slug: "konflikt",
    num: 7,
    title: "Konflikte",
    parallel: true,
    steps: [
      { num: 1, label: "Conflict-Detection nach OCR" },
      { num: 2, label: "Resolver sichtbar (Phase 2/7 oder /conflicts)" },
      // Step 3: BRANCHING — die User-Aktion entscheidet
      {
        num: 3,
        label: "Resolve: overwrite / discard / reassign",
        userGate: true,
        branches: [
          { when: (c) => c.state === "ocr_complete" || POST_VALIDATION.has(c.state),
                                                 to: { kind: "step", phase: "validierung", step: 1 }, label: "overwrite/reassign → Validierung" },
          { when: (c) => c.state === "archived", to: { kind: "end" },                                  label: "discard → Karte beendet" },
          { when: (c) => c.state === "superseded", to: { kind: "end" },                                label: "superseded → Karte beendet" },
          { when: (c) => c.state === "pending_conflict", to: { kind: "wait" },                         label: "User-Entscheidung erforderlich" },
          { when: () => true,                    to: { kind: "wait" },                                  label: "kein aktiver Konflikt" },
        ],
      },
    ],
    done: (c) => c.state !== "pending_conflict",
    endTransitions: [],
  },
];

// ─── Lookup API ───────────────────────────────────────────────────────────

export function phaseBySlug(slug: PhaseSlug): PhaseFlow | null {
  return PIPELINE_FLOW.find(p => p.slug === slug) ?? null;
}

export interface NextPhaseResolution {
  type: "phase" | "end" | "wait";
  target?: PhaseSlug;
  num?: number;
  label: string;
}

/** Resolve the next step for a given (phase, card, timesheet). Uses last step's
 * branches if it has any, otherwise phase-level endTransitions. */
export function nextPhaseFor(
  current: PhaseSlug,
  card: Card | null,
  timesheet: Timesheet | null,
): NextPhaseResolution {
  if (!card) return { type: "wait", label: "Keine Karte ausgewählt" };
  const flow = phaseBySlug(current);
  if (!flow) return { type: "wait", label: `Unbekannte Phase ${current}` };

  // Try last step's branches first, then fall back to phase-level
  const lastStep = flow.steps[flow.steps.length - 1];
  const transitions = lastStep?.branches?.length ? lastStep.branches : flow.endTransitions;

  for (const t of transitions) {
    if (t.when(card, timesheet)) {
      if (t.to.kind === "end") return { type: "end", label: t.label };
      if (t.to.kind === "wait") return { type: "wait", label: t.label };
      const targetPhase = phaseBySlug(t.to.phase);
      return {
        type: "phase",
        target: t.to.phase,
        num: targetPhase?.num,
        label: t.label,
      };
    }
  }
  return { type: "wait", label: "Keine matchende Transition" };
}

// phaseDone/phaseReachable leiten den Tab-Status seit 2026-06-09 aus dem
// Raum-Contract-Modell (rooms.ts, Single Source) ab — ehrliche Twin-Reise statt
// State-Heuristik. Der `timesheet`-Parameter bleibt aus Signatur-Kompat erhalten
// (computeJourney liest data/state/kind direkt aus der Karte, auch für Expense).
export function phaseDone(slug: PhaseSlug, card: Card | null, _timesheet: Timesheet | null): boolean {
  if (!card) return false;
  const st = roomStatus(card as unknown as RoomCardLike, (card.kind ?? "timesheet") as RoomCardKind, slug);
  return st?.status === "done";
}

/** Voller Raum-Status der Phase (für grün/rot/⚪-Visualisierung). */
export function phaseStatus(slug: PhaseSlug, card: Card | null): RoomStatus | null {
  if (!card) return null;
  return roomStatus(card as unknown as RoomCardLike, (card.kind ?? "timesheet") as RoomCardKind, slug)?.status ?? null;
}

export function phaseReachable(slug: PhaseSlug, card: Card | null, _timesheet: Timesheet | null): boolean {
  if (slug === "upload") return true;
  if (!card) return false;
  if (slug === "konflikt") return true; // paralleler Resolver-Tab immer offenbar
  // Erreichbar = der Twin war/ist in diesem Raum (done/active/error). Räume, die
  // der Twin nie erreicht hat (not-reached), sind disabled.
  const st = roomStatus(card as unknown as RoomCardLike, (card.kind ?? "timesheet") as RoomCardKind, slug);
  return st != null && st.status !== "not-reached";
}

/** @deprecated Tab-Status kommt jetzt aus rooms.ts. Diese Funktion bleibt nur
 *  für das Mermaid-Flow-Diagramm (Transitions). Nicht für Status verwenden. */
function _legacyPhaseReachable(slug: PhaseSlug, card: Card | null, timesheet: Timesheet | null): boolean {
  const flow = phaseBySlug(slug);
  if (!flow) return false;
  if (flow.parallel) return !!card;
  if (!card) return slug === "upload";
  if (slug === "upload") return true;
  if (TERMINAL.has(card.state)) {
    if (slug === "ocr") return true;
    return flow.done(card, timesheet);
  }
  for (const f of PIPELINE_FLOW) {
    if (f.slug === slug) return true;
    if (f.parallel) continue;
    if (!f.done(card, timesheet)) return false;
  }
  return true;
}
